/**
 * Validates a returnTo URL to prevent open redirect vulnerabilities.
 *
 * @param returnTo - The URL to validate
 * @returns The validated URL if safe, or null if invalid/unsafe
 *
 * Security rules:
 * - Must be a relative URL (starts with /)
 * - Cannot contain protocol (no http:, https:, javascript:, etc.)
 * - Cannot contain double slashes at start (//evil.com)
 * - Cannot contain backslashes (path traversal)
 * - Cannot contain null bytes
 */
export function validateReturnTo(returnTo: string | null | undefined): string | null {
  if (!returnTo || typeof returnTo !== "string") {
    return null;
  }

  const trimmed = returnTo.trim();

  // Must start with a single forward slash (relative path)
  if (!trimmed.startsWith("/")) {
    return null;
  }

  // Reject protocol-relative URLs (//evil.com)
  if (trimmed.startsWith("//")) {
    return null;
  }

  // Reject URLs with protocols (javascript:, http:, https:, data:, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(trimmed)) {
    return null;
  }

  // Reject backslashes (can be used for path traversal or protocol confusion)
  if (trimmed.includes("\\")) {
    return null;
  }

  // Reject null bytes
  if (trimmed.includes("\0")) {
    return null;
  }

  // Reject URLs that could be interpreted as protocol-relative after decoding
  try {
    const decoded = decodeURIComponent(trimmed);
    if (decoded.startsWith("//") || /^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(decoded)) {
      return null;
    }
  } catch {
    // If decoding fails, reject the URL
    return null;
  }

  return trimmed;
}

/**
 * Gets a safe returnTo URL, falling back to default if invalid
 *
 * @param returnTo - The URL to validate
 * @param defaultPath - The fallback path (default: "/files")
 * @returns A safe URL to redirect to
 */
export function getSafeReturnTo(returnTo: string | null | undefined, defaultPath = "/files"): string {
  return validateReturnTo(returnTo) || defaultPath;
}
